/**
 * CS-1602 — where an agent's temp files go, as one value both spawn paths read.
 *
 * DELIBERATELY ITS OWN MODULE, and it stays narrow: where the agent's temp
 * directory is, and making sure that path is usable. `claudecode/index.ts`
 * is the whole provisioning graph; importing it from the CLI to get a path join
 * would drag identity templates, MCP rendering and the artifact deployer along
 * with it. Same reason `identity.js` is separate and why the provisioning barrel
 * re-exports from there rather than from the index.
 *
 * THE REASON IT IS SHARED RATHER THAN JOINED TWICE. The bare-host (tmux) spawn
 * and the docker spawn each build their own env, and ENG-8344 is what happens
 * when a per-agent value is stamped on one and not the other: `AGT_AGENT_ID`
 * was on the docker argv and nowhere else, so every host-spawned agent read an
 * empty string for a year. Two call sites joining `'scratch', 'tmp'`
 * independently is the same bug waiting on a rename.
 */

import { closeSync, lstatSync, mkdirSync, openSync, renameSync, type Stats } from 'node:fs';
import { dirname, join } from 'node:path';

/**
 * The agent's private temp directory, given its agent directory.
 *
 * Under `scratch/` on purpose: `sweepScratchDir` already reclaims scratch
 * entries after the retention window, and its freshness check walks the tree
 * (ENG-8907), so a temp dir being actively written stays and a dormant one ages
 * out. Shared `/tmp/claude-0` has no sweep at all, which is how one agent
 * accumulated 2.7G and ENOSPC'd every other agent on the host.
 */
export function agentTmpDirFor(agentDir: string): string {
  return join(agentDir, 'scratch', 'tmp');
}

/**
 * The env var Claude Code reads for its temp directory.
 *
 * Named here so the CLI, the provisioner and any test all spell it the same
 * way. Before CS-1602 this string appeared ZERO times in the repository: the
 * shared-`/tmp` default was never a misconfiguration to correct, it was a
 * default nobody had overridden.
 */
export const CLAUDE_CODE_TMPDIR_ENV = 'CLAUDE_CODE_TMPDIR';

/**
 * What {@link ensureAgentTmpDir} found at the temp path, and what it did.
 *
 * `displaced` is the interesting one and is deliberately not folded into
 * `created`: it means agent-written content was in the way, which an operator
 * reading a log line needs to see named.
 */
export type AgentTmpDirOutcome =
  | { outcome: 'ready'; path: string }
  | { outcome: 'created'; path: string }
  | { outcome: 'displaced'; path: string; displacedTo: string; kind: string }
  | { outcome: 'failed'; path: string; error: string };

/**
 * ENG-9614 — make the agent's temp path usable before a spawn reads it.
 *
 * THE INCIDENT. `scratch/` is agent-writable by design, and this module puts
 * the temp dir at a fixed, guessable path inside it. On prod host
 * `i-0f890e0a389d04511`, agent `horatio` wrote a 37,430-byte document (the
 * customer's anti-bribery policy) to `scratch/tmp` on 2026-08-24, evidently
 * using `tmp` as an ordinary scratch filename. It was harmless for six days.
 * It became fatal the moment the host self-updated onto a CLI whose temp path
 * collides with it: Claude Code mkdirs `<CLAUDE_CODE_TMPDIR>/claude-0` at
 * startup, hit `ENOTDIR`, and died instantly on every spawn - an unrecoverable
 * ~21s respawn loop that only stopped when an operator moved the file aside.
 *
 * ARMED AT WRITE TIME, DETONATES AT THE NEXT RESTART - whatever triggers that
 * restart, including the ordinary maintenance-window self-update. So the agent
 * that creates the landmine is not the one that steps on it, and the two events
 * can be days apart with nothing connecting them.
 *
 * WHY REPAIR RATHER THAN RELOCATE. Moving temp out of `scratch/` would dodge
 * the collision and undo the second half of CS-1602: `sweepScratchDir` reclaims
 * scratch entries and its freshness check walks the tree, which is the only
 * reason this temp dir is swept at all. `/tmp/claude-0` had no sweep and
 * accumulated 2.7G. The location is right; it just needed a guard.
 *
 * WHY IT DISPLACES AND NEVER DELETES. The file in the live incident was the
 * customer's own document, and a spawn-time cleanup that deletes agent-written
 * content destroys work nobody agreed to lose. This is the same asymmetry
 * `isFreshWithin` states for the sweep: keeping something stale costs disk,
 * which is cheap and measured; deleting something live costs uncommitted work,
 * which is neither. The operator who fixed the live host reached for `mv -n`
 * for exactly this reason.
 *
 * WHY `lstatSync` AND NOT `statSync`. A SYMLINK at `scratch/tmp` pointing at,
 * say, `/root/.claude` does not fail - `mkdir claude-0` follows it and
 * SUCCEEDS, silently relocating every agent temp file outside the scratch tree
 * (unswept, and outside the tenancy boundary `getScratchDir` exists to draw).
 * That is a quieter failure than the ENOTDIR and is displaced for the same
 * reason. `statSync` would report the symlink as a directory and wave it past.
 *
 * Node-only (`node:fs`), which this module already was via its consumers - it
 * is deliberately off the browser-reachable provisioning barrel (see the
 * CS-1602 note in `provisioning/index.ts`), so reaching for fs here changes
 * nothing about who can import it.
 *
 * Never throws. A spawn must not be aborted by a repair that did not work; the
 * `failed` outcome carries the reason so the caller can log a diagnostic that
 * names the path, which is the whole complaint against the bare ENOTDIR.
 */
export function ensureAgentTmpDir(
  agentDir: string,
  now: Date = new Date(),
): AgentTmpDirOutcome {
  const path = agentTmpDirFor(agentDir);

  // CodeRabbit (PR #5149) — the leaf check below is not sufficient on its own.
  //
  // `lstatSync(path)` inspects only the final `tmp` entry. If `scratch` ITSELF
  // is a symlink, that lstat reports on the link's target and
  // `mkdirSync(path, { recursive: true })` FOLLOWS it, creating `tmp` outside
  // the agent directory entirely — the same escape the symlink-at-`tmp` branch
  // below exists to close, one component up, and silent in exactly the same way.
  //
  // REPORTED, NOT DISPLACED, unlike the leaf. A symlink at `tmp` is agent-
  // written content with no legitimate reason to exist. A symlink at `scratch`
  // may well be an OPERATOR's, pointing a disk-constrained host's scratch at a
  // larger volume — displacing that would break a deliberate arrangement to
  // defend against a hypothetical one. Reporting names the condition and leaves
  // the decision with the person who created it.
  //
  // Note what this does and does not buy: it does not PREVENT the escape (a
  // spawn still proceeds, and Claude Code will create the directory through the
  // link itself). It converts a silent tenancy violation into a logged one.
  const scratchDir = dirname(path);
  try {
    if (lstatSync(scratchDir).isSymbolicLink()) {
      return {
        outcome: 'failed',
        path,
        error: `${scratchDir} is a symlink; refusing to create a temp directory through it (agent temp files would land outside the agent tree)`,
      };
    }
  } catch (err) {
    // ENOENT is the first-ever-spawn case; `mkdirSync(recursive)` creates it.
    if ((err as NodeJS.ErrnoException)?.code !== 'ENOENT') {
      return { outcome: 'failed', path, error: describeError(err) };
    }
  }

  let existing: Stats | null;
  try {
    existing = lstatSync(path);
  } catch (err) {
    // ENOENT is the ordinary first-spawn case, not a problem. Anything else is
    // a real failure to observe the path, and must not be reported as absence.
    if ((err as NodeJS.ErrnoException)?.code !== 'ENOENT') {
      return { outcome: 'failed', path, error: describeError(err) };
    }
    existing = null;
  }

  if (existing?.isDirectory()) return { outcome: 'ready', path };

  let displacedTo: string | null = null;
  if (existing) {
    const kind = describeEntryKind(existing);
    try {
      // The reservation is what makes the rename safe — see below.
      displacedTo = reserveDisplacementPath(path, now);
      renameSync(path, displacedTo);
    } catch (err) {
      return { outcome: 'failed', path, error: describeError(err) };
    }
    try {
      mkdirSync(path, { recursive: true });
    } catch (err) {
      return { outcome: 'failed', path, error: describeError(err) };
    }
    return { outcome: 'displaced', path, displacedTo, kind };
  }

  try {
    // `recursive` also creates `scratch/` on a first-ever spawn. If an ANCESTOR
    // is itself a non-directory this throws ENOTDIR naming that component - a
    // far rarer shape than the leaf collision, and one this function reports
    // rather than operates on: blind surgery further up an agent's tree is a
    // worse failure than a spawn that says exactly what is wrong.
    mkdirSync(path, { recursive: true });
  } catch (err) {
    return { outcome: 'failed', path, error: describeError(err) };
  }
  return { outcome: 'created', path };
}

/** Human-readable name for what was squatting on the temp path. */
function describeEntryKind(stats: Stats): string {
  if (stats.isSymbolicLink()) return 'symlink';
  if (stats.isFile()) return 'regular file';
  if (stats.isFIFO()) return 'fifo';
  if (stats.isSocket()) return 'socket';
  if (stats.isBlockDevice()) return 'block device';
  if (stats.isCharacterDevice()) return 'character device';
  return 'non-directory';
}

/**
 * ATOMICALLY reserve a free `tmp.displaced-<stamp>` sibling, and return it.
 *
 * CodeRabbit (PR #5149) — the first version of this was `existsSync(candidate)`
 * followed by the caller's `renameSync`, which is wrong twice over:
 *
 *   - `rename(2)` OVERWRITES an existing destination silently. So the guarantee
 *     this function exists to provide - never destroy an earlier displacement -
 *     held only for as long as nothing else touched the directory.
 *   - check-then-act is a TOCTOU window. Two managers displacing in the same
 *     second (or one racing an agent still writing to scratch) both see the
 *     name free, and the second rename destroys the first's file. That the
 *     window is small does not help: the whole reason this code path exists is
 *     that a rare collision already cost a customer their document once.
 *
 * `openSync(candidate, 'wx')` is the fix: O_CREAT|O_EXCL is atomic at the
 * kernel, so exactly one caller can win a given name and the loser gets EEXIST
 * and moves on. The caller then renames ONTO the zero-byte placeholder it just
 * won - which `rename(2)` will happily replace, because it is ours.
 *
 * Safe for every source this is reached with: the caller only displaces
 * NON-directories, and `rename(2)` permits replacing a regular file with
 * another non-directory. (A directory source would fail ENOTDIR - correctly,
 * since a directory at `tmp` is the healthy case and never gets here.)
 *
 * Bounded so a pathological tree cannot spin - past the bound the caller
 * reports a failure, which is the honest outcome.
 */
function reserveDisplacementPath(path: string, now: Date): string {
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z');
  const base = `${path}.displaced-${stamp}`;
  for (let n = 1; n <= 100; n += 1) {
    const candidate = n === 1 ? base : `${base}-${n}`;
    try {
      closeSync(openSync(candidate, 'wx'));
      return candidate;
    } catch (err) {
      if ((err as NodeJS.ErrnoException)?.code !== 'EEXIST') throw err;
    }
  }
  throw new Error(`no free displacement name beside ${path}`);
}

function describeError(err: unknown): string {
  const code = (err as NodeJS.ErrnoException)?.code;
  const msg = err instanceof Error ? err.message : String(err);
  return code ? `${code}: ${msg}` : msg;
}
